/**
 * Careers.jsx — Phase 143 — Public Presence Consolidation.
 * /careers is now the SINGLE public destination for Analogy's brand story
 * AND hiring. One continuous scroll: Studio Soul (Vision) -> Design
 * Principles -> Day in the Life (Gallery) -> Peer Endorsements -> Live Job
 * Openings -> Apply. Everything above the job list is 100% CMS-driven from
 * GET /public/studio-content (edited by the Founder in Registry > Public
 * Branding — see PublicBrandingTab.jsx); jobs come from GET /public/careers.
 *
 * Visual language (Phase 161 — editorial redesign, matched to
 * analogydesign.co): white body sections, near-black (#1c1c1c) text, brand
 * red (#F54800) used ONLY for headline emphasis words and interactive
 * links/arrows — no other colours, no gradients, no card borders or box
 * shadows on content. Space Grotesk carries the heavy editorial headlines,
 * Poppins carries body copy, via the `.studio-page` class in index.css —
 * deliberately does NOT touch the internal app's Cabinet Grotesk / IBM Plex
 * Sans typography. The dark (#1c1c1c) top nav + footer frame the page and
 * stay dark by design, matching the minimal nav / dark footer used across
 * the rest of the app. /our-studio redirects here (see App.js).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import AnalogyLogo from "@/components/AnalogyLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, MapPin, Briefcase, CheckCircle2, Linkedin, MessageCircle, Link2, Check } from "lucide-react";

const NOTICE_PERIOD_OPTIONS = ["Immediate", "15 Days", "30 Days", "60 Days", "90 Days"];
const DEFAULT_COVER = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80";

/* ---------- Share bar — links point at the crawler-friendly OG page ---------- */
function RoleShareBar({ role }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${API}/public/careers/${role.id}/share`;
  const text = `${role.title} — Careers at Analogy Design`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — long-press the link instead"); }
  };
  const cls = "inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 hover:text-[#F54800] transition-colors";
  return (
    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="role-share-bar">
      <span className="text-xs uppercase tracking-[0.2em] text-slate-400">Share</span>
      <a href={linkedin} target="_blank" rel="noopener noreferrer" className={cls} data-testid="role-share-linkedin"><Linkedin size={14} /> LinkedIn</a>
      <a href={whatsapp} target="_blank" rel="noopener noreferrer" className={cls} data-testid="role-share-whatsapp"><MessageCircle size={14} /> WhatsApp</a>
      <button type="button" onClick={copy} className={cls} data-testid="role-share-copy">
        {copied ? <Check size={14} className="text-emerald-600" /> : <Link2 size={14} />} {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}

/* ---------- Shared editorial primitives ---------- */
function Eyebrow({ children }) {
  return <div className="text-xs font-semibold uppercase tracking-[0.25em] mb-4 text-[#F54800]">{children}</div>;
}

// Renders a heading with 1-2 key words emphasised in brand red. Supports an
// explicit `{{word}}` marker (used for static copy below, and honoured if an
// admin adds it to CMS text); otherwise falls back to highlighting the
// trailing `highlightLast` words so CMS-only headlines still get emphasis.
function Highlighted({ text, highlightLast = 2 }) {
  if (!text) return null;
  if (text.includes("{{")) {
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return (
      <>
        {parts.map((part, i) => {
          const m = part.match(/^\{\{(.+)\}\}$/);
          return m ? <span key={i} className="text-[#F54800]">{m[1]}</span> : <React.Fragment key={i}>{part}</React.Fragment>;
        })}
      </>
    );
  }
  const words = text.trim().split(/\s+/);
  if (words.length <= highlightLast) return <span className="text-[#F54800]">{text}</span>;
  const head = words.slice(0, words.length - highlightLast).join(" ");
  const tail = words.slice(words.length - highlightLast).join(" ");
  return <>{head} <span className="text-[#F54800]">{tail}</span></>;
}

/* ---------- Header / Footer (dark, minimal — frames the white body) ---------- */
function CareersHeader() {
  return (
    <header className="sticky top-0 z-20 bg-[#1c1c1c] px-6 sm:px-10 py-5" data-testid="careers-header">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <a href="https://www.analogydesign.co" className="text-white hover:text-[#F54800] transition-colors" aria-label="Analogy Design">
          <AnalogyLogo size={24} />
        </a>
        <a href="https://www.analogydesign.co" className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 hover:text-[#F54800] transition-colors inline-flex items-center gap-1.5" data-testid="careers-header-site-link">
          analogydesign.co <ArrowUpRight size={13} />
        </a>
      </div>
    </header>
  );
}

function CareersFooter() {
  return (
    <footer className="bg-[#1c1c1c] px-6 sm:px-10 pt-16 pb-8" data-testid="careers-footer">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr] gap-10 sm:gap-8 pb-12 border-b border-white/10">
          <div>
            <a href="https://www.analogydesign.co" className="inline-block text-white hover:text-[#F54800] transition-colors" aria-label="Analogy Design">
              <AnalogyLogo size={22} />
            </a>
            <p className="text-white/40 text-sm mt-4 max-w-xs leading-relaxed">
              A product design and engineering studio building world-first hardware out of Bengaluru.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-4">Studio</div>
            <ul className="space-y-2.5 text-sm">
              <li><a href="https://www.analogydesign.co" className="text-white/70 hover:text-[#F54800] transition-colors" data-testid="careers-footer-site-link">analogydesign.co</a></li>
              <li><Link to="/careers" className="text-white/70 hover:text-[#F54800] transition-colors" data-testid="careers-footer-careers-link">Open roles</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-4">Contact</div>
            <ul className="space-y-2.5 text-sm">
              <li><a href="mailto:hrd@analogydesign.co" className="text-white/70 hover:text-[#F54800] transition-colors" data-testid="careers-footer-email-link">hrd@analogydesign.co</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 text-white/30 text-xs" data-testid="careers-footer-copyright">© {new Date().getFullYear()} Analogy Design. All rights reserved.</div>
      </div>
    </footer>
  );
}

/* ---------- Studio Soul (Vision / Mission) ---------- */
function VisionSection({ vision }) {
  if (!vision.headline && !vision.mission && !vision.vision) return null;
  return (
    <section className="bg-white px-6 sm:px-10 py-24 sm:py-32" data-testid="careers-vision-section">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>{vision.eyebrow || "The Soul of the Studio"}</Eyebrow>
        <h1 className="text-[#1c1c1c] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] max-w-4xl" data-testid="careers-vision-headline">
          <Highlighted text={vision.headline || "We build world-firsts. Help us build the next one."} highlightLast={3} />
        </h1>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-black/10 pt-10">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-3">Mission</div>
            <p className="text-slate-700 text-lg leading-relaxed" data-testid="careers-vision-mission">{vision.mission}</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-3">Vision</div>
            <p className="text-slate-700 text-lg leading-relaxed" data-testid="careers-vision-vision">{vision.vision}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Design Principles ---------- */
function PrinciplesSection({ principles }) {
  if (principles.length === 0) return null;
  return (
    <section className="bg-white border-t border-black/5 px-6 sm:px-10 py-24 sm:py-28" data-testid="careers-principles-section">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>The Philosophy</Eyebrow>
        <h2 className="text-[#1c1c1c] text-3xl sm:text-4xl font-semibold max-w-2xl"><Highlighted text="Our design {{principles}}" /></h2>
        <div className="mt-14 divide-y divide-black/10">
          {principles.map((p, i) => (
            <div key={i} className="grid grid-cols-[auto_1fr] sm:grid-cols-[100px_1fr] gap-4 sm:gap-8 py-7 group" data-testid={`careers-principle-${i}`}>
              <div className="text-[#F54800]/30 group-hover:text-[#F54800] transition-colors text-3xl sm:text-4xl font-semibold tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div>
                <div className="text-[#1c1c1c] font-semibold text-lg">{p.title}</div>
                <div className="text-slate-500 text-sm mt-1.5 leading-relaxed max-w-xl">{p.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Day in the Life (Gallery) ---------- */
function GallerySection({ gallery }) {
  if (gallery.length === 0) return null;
  return (
    <section className="bg-white border-t border-black/5 px-6 sm:px-10 py-24 sm:py-28" data-testid="careers-gallery-section">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Visual Evidence</Eyebrow>
        <h2 className="text-[#1c1c1c] text-3xl sm:text-4xl font-semibold max-w-2xl"><Highlighted text="A day in the {{life}}" /></h2>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 auto-rows-[180px] sm:auto-rows-[220px]">
          {gallery.map((g, i) => (
            <div key={i} className={`relative overflow-hidden group ${i % 5 === 0 ? "col-span-2 row-span-2" : ""}`} data-testid={`careers-gallery-item-${i}`}>
              <img
                src={g.image_url}
                alt={g.caption || "Life at Analogy"}
                loading="lazy"
                className="w-full h-full object-cover grayscale-[20%] saturate-[0.85] group-hover:scale-105 group-hover:grayscale-0 group-hover:saturate-100 transition-all duration-500"
              />
              {g.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-white text-xs">{g.caption}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Peer Endorsements ---------- */
function PeerEndorsementsSection({ items }) {
  if (items.length === 0) return null;
  return (
    <section className="bg-white border-t border-black/5 px-6 sm:px-10 py-24 sm:py-28" data-testid="careers-endorsements-section">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Peer Endorsements</Eyebrow>
        <h2 className="text-[#1c1c1c] text-3xl sm:text-4xl font-semibold max-w-2xl"><Highlighted text="What industry leaders {{say}}" /></h2>
        <div className="mt-14 divide-y divide-black/10">
          {items.map((p, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4 sm:gap-10 py-8" data-testid={`careers-endorsement-${i}`}>
              <div className="flex items-center gap-3">
                {p.photo_url && (
                  <img src={p.photo_url} alt={p.name} className="w-12 h-12 rounded-full object-cover grayscale-[20%] saturate-[0.85]" />
                )}
                <div>
                  <div className="text-sm font-semibold text-[#1c1c1c]">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.title_company}</div>
                </div>
              </div>
              <p className="text-slate-700 text-lg leading-relaxed">&ldquo;{p.quote}&rdquo;</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Live Job Openings ---------- */
function RoleCover({ src, alt }) {
  return (
    <div className="aspect-video w-full bg-black/5 overflow-hidden">
      <img
        src={src || DEFAULT_COVER}
        alt={alt || "Role cover"}
        loading="lazy"
        className="w-full h-full object-cover grayscale-[15%] saturate-[0.85]"
        onError={(e) => { if (e.currentTarget.src !== DEFAULT_COVER) e.currentTarget.src = DEFAULT_COVER; }}
      />
    </div>
  );
}

function JobOpeningsSection({ roles }) {
  return (
    <section className="bg-white border-t border-black/5 px-6 sm:px-10 py-24 sm:py-32" data-testid="careers-jobs-section">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>Join Us</Eyebrow>
        <h2 className="text-[#1c1c1c] text-3xl sm:text-4xl font-semibold max-w-2xl"><Highlighted text="Live job {{openings}}" /></h2>
        <p className="text-slate-500 text-base mt-4 max-w-xl leading-relaxed">
          We are looking for industrial designers, engineers and strategists who think in prototypes, not slides.
        </p>

        <div className="mt-14 border-t border-black/10">
          {roles.map((r) => (
            <Link
              to={`/careers/${r.id}`}
              key={r.id}
              className="group flex items-center justify-between gap-6 py-8 border-b border-black/10 hover:bg-black/[0.015] transition-colors px-0"
              data-testid={`role-${r.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 mb-3 flex-wrap">
                  <span className="text-[11px] uppercase tracking-widest text-[#F54800] font-semibold">{r.business_unit || "Studio"}</span>
                  <span className="text-[11px] uppercase tracking-widest text-slate-400">·</span>
                  <span className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">{r.employment_type}</span>
                  <span className="text-[11px] uppercase tracking-widest text-slate-400">·</span>
                  <span className="text-[11px] uppercase tracking-widest text-slate-500 font-medium inline-flex items-center gap-1"><MapPin size={11} /> {r.location}</span>
                </div>
                <h3 className="text-[#1c1c1c] text-2xl sm:text-3xl font-semibold group-hover:text-[#F54800] transition-colors">{r.title}</h3>
                <p className="text-sm text-slate-500 mt-2 max-w-2xl line-clamp-2">{r.role_overview || r.description}</p>
              </div>
              <ArrowUpRight
                size={28}
                className="shrink-0 text-[#1c1c1c] group-hover:text-[#F54800] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
              />
            </Link>
          ))}
          {roles.length === 0 && (
            <div className="py-16 text-center border-b border-black/10" data-testid="careers-no-roles">
              <Briefcase size={40} className="mx-auto text-slate-300 mb-3" />
              <div className="text-[#1c1c1c] text-xl font-semibold">No open roles right now</div>
              <p className="text-sm text-slate-500 mt-2">Drop us a line — we always want to meet sharp people.</p>
            </div>
          )}
        </div>

        <div className="mt-10 text-sm text-slate-500">
          <p>Curious but don&apos;t see your role? Email <a className="text-[#F54800] hover:underline" href="mailto:hrd@analogydesign.co" data-testid="careers-fallback-email-link">hrd@analogydesign.co</a></p>
        </div>
      </div>
    </section>
  );
}

/* ---------- /careers — the single master page ---------- */
export function CareersIndex() {
  const [c, setC] = useState(null);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get("/public/studio-content").then(({ data }) => setC(data)).catch(() => setC({}));
    api.get("/public/careers").then(({ data }) => setRoles(data.roles || [])).catch(() => setRoles([]));
  }, []);

  if (!c) {
    return <div className="min-h-screen bg-[#1c1c1c] flex items-center justify-center text-white/40 text-sm">Loading…</div>;
  }

  return (
    <div className="studio-page min-h-screen bg-white" data-testid="careers-page">
      <CareersHeader />
      <VisionSection vision={c.vision || {}} />
      <PrinciplesSection principles={c.principles || []} />
      <GallerySection gallery={c.gallery || []} />
      <PeerEndorsementsSection items={c.peer_endorsements || []} />
      <JobOpeningsSection roles={roles} />
      <CareersFooter />
    </div>
  );
}

/* ---------- /careers/:roleId — Apply flow ---------- */
export function CareersApply() {
  const { roleId } = useParams();
  const nav = useNavigate();
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", cover_note: "", resume: null,
    current_salary: "", notice_period: "", portfolio: null,
    answer_why_join: "", answer_challenging_project: "", answer_expertise: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public/careers").then(({ data }) => {
      const r = (data.roles || []).find((x) => x.id === roleId);
      if (!r) { nav("/careers"); return; }
      setRole(r);
      document.title = `${r.title} — Careers at Analogy Design`;
    }).catch(() => nav("/careers"));
    return () => { document.title = "Careers at Analogy Design"; };
  }, [roleId, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.resume) { toast.error("Attach your resume"); return; }
    if (form.current_salary === "" || Number(form.current_salary) < 0) { toast.error("Current salary is required"); return; }
    if (!form.notice_period) { toast.error("Notice period is required"); return; }
    if (!form.answer_why_join.trim() || !form.answer_challenging_project.trim() || !form.answer_expertise.trim()) {
      toast.error("Please answer all 3 application questions");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("role_id", roleId);
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      if (form.cover_note) fd.append("cover_note", form.cover_note);
      fd.append("current_salary", form.current_salary);
      fd.append("notice_period", form.notice_period);
      fd.append("answer_why_join", form.answer_why_join);
      fd.append("answer_challenging_project", form.answer_challenging_project);
      fd.append("answer_expertise", form.answer_expertise);
      fd.append("resume", form.resume);
      if (form.portfolio) fd.append("portfolio", form.portfolio);
      await api.post("/public/careers/apply", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!role) return <div className="studio-page min-h-screen bg-[#1c1c1c] flex items-center justify-center text-white/40 text-sm">Loading…</div>;

  return (
    <div className="studio-page min-h-screen bg-white" data-testid="careers-apply-page">
      <CareersHeader />
      <main className="px-6 sm:px-10 py-16 max-w-3xl mx-auto">
        <Link to="/careers" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 hover:text-[#F54800] transition-colors" data-testid="careers-apply-back-link">← All open roles</Link>
        <div className="mt-6 mb-8">
          <RoleCover src={role.cover_image_url} alt={role.title} />
        </div>
        <div className="mb-12">
          <Eyebrow>{role.business_unit || "Studio"} · {role.employment_type}</Eyebrow>
          <h1 className="text-[#1c1c1c] text-4xl sm:text-5xl font-semibold leading-[1.08]" data-testid="careers-apply-role-title">{role.title}</h1>
          <div className="text-xs text-slate-500 mt-4 inline-flex items-center gap-1"><MapPin size={12} /> {role.location}</div>
          <RoleShareBar role={role} />
          <RoleSection title="Role Overview">{role.role_overview || role.description}</RoleSection>
          <RoleSection title="Responsibilities">{role.responsibilities}</RoleSection>
          <RoleSection title="Requirements">{role.requirements}</RoleSection>
          <RoleSection title="Qualifications">{role.qualifications}</RoleSection>
        </div>

        {done ? (
          <div className="border-t border-black/10 pt-10 text-center" data-testid="apply-success">
            <CheckCircle2 size={36} className="text-emerald-600 mx-auto" />
            <div className="text-[#1c1c1c] text-2xl font-semibold mt-3">Thanks — we have your application.</div>
            <p className="text-sm text-slate-500 mt-2">We&apos;ll be in touch within 7 working days.</p>
            <Link to="/careers" className="inline-block mt-6 text-[#F54800] text-sm hover:underline">View other open roles →</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border-t border-black/10 pt-8 space-y-4" data-testid="apply-form">
            <div className="text-[#1c1c1c] text-lg font-semibold">Apply</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Full name</Label><Input required className="rounded-sm mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" /></div>
              <div><Label className="label-overline">Email</Label><Input required type="email" className="rounded-sm mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" /></div>
            </div>
            <div><Label className="label-overline">Phone</Label><Input className="rounded-sm mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Current salary (₹) *</Label>
                <Input type="number" min="0" className="rounded-sm mt-1" value={form.current_salary} onChange={(e) => setForm({ ...form, current_salary: e.target.value })} data-testid="apply-current-salary" />
              </div>
              <div>
                <Label className="label-overline">Notice period *</Label>
                <Select value={form.notice_period} onValueChange={(v) => setForm({ ...form, notice_period: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="apply-notice-period"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {NOTICE_PERIOD_OPTIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="label-overline">Cover note</Label><Textarea rows={4} className="rounded-sm mt-1" placeholder="Why this role?" value={form.cover_note} onChange={(e) => setForm({ ...form, cover_note: e.target.value })} data-testid="apply-note" /></div>
            <div className="border-t border-black/10 pt-4 space-y-4">
              <div className="text-[#1c1c1c] text-sm font-semibold">A few questions</div>
              <div>
                <Label className="label-overline">Why do you want to join Analogy Design?</Label>
                <Textarea required rows={3} className="rounded-sm mt-1" value={form.answer_why_join} onChange={(e) => setForm({ ...form, answer_why_join: e.target.value })} data-testid="apply-answer-why-join" />
              </div>
              <div>
                <Label className="label-overline">What is your most challenging project to date?</Label>
                <Textarea required rows={3} className="rounded-sm mt-1" value={form.answer_challenging_project} onChange={(e) => setForm({ ...form, answer_challenging_project: e.target.value })} data-testid="apply-answer-challenging-project" />
              </div>
              <div>
                <Label className="label-overline">What design/engineering micro-offer are you most expert in?</Label>
                <Textarea required rows={3} className="rounded-sm mt-1" value={form.answer_expertise} onChange={(e) => setForm({ ...form, answer_expertise: e.target.value })} data-testid="apply-answer-expertise" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Resume (PDF / DOCX, ≤10 MB)</Label>
                <Input required type="file" accept=".pdf,.doc,.docx" className="rounded-sm mt-1" onChange={(e) => setForm({ ...form, resume: e.target.files?.[0] || null })} data-testid="apply-resume" />
              </div>
              <div>
                <Label className="label-overline">Portfolio (optional, ≤10 MB)</Label>
                <Input type="file" accept=".pdf,.doc,.docx,.zip,image/*" className="rounded-sm mt-1" onChange={(e) => setForm({ ...form, portfolio: e.target.files?.[0] || null })} data-testid="apply-portfolio" />
              </div>
            </div>
            <Button disabled={submitting} type="submit" className="w-full rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white h-11" data-testid="apply-submit">{submitting ? "Submitting…" : "Submit application"}</Button>
            <p className="text-xs text-slate-400">By submitting, you consent to Analogy Design storing your resume for hiring purposes only.</p>
          </form>
        )}
      </main>
      <CareersFooter />
    </div>
  );
}

function RoleSection({ title, children }) {
  if (!children || (typeof children === "string" && !children.trim())) return null;
  return (
    <section className="mt-8" data-testid={`role-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="text-[#1c1c1c] text-xl font-semibold mb-3">{title}</h2>
      <div className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm">{children}</div>
    </section>
  );
}
